import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dataService } from '@/services/dataService';
import { Creator } from '@/types/entities';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { CopyButton } from '@/components/ui/CopyButton';
import { SafeLink } from '@/components/ui/SafeLink';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useI18n } from '@/i18n';
import { useToast } from '@/components/ui/Toast';
import { pushRecentlyViewed } from '@/lib/recentlyViewed';
import { exportRowsToCsv } from '@/lib/exportCsv';
import { CreatorFormDrawer, CreatorFormValues } from './CreatorFormDrawer';

export function CreatorDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const push = useToast();
  const [creator, setCreator] = useState<Creator | null | undefined>(undefined);
  const [editing, setEditing] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [noteText, setNoteText] = useState('');

  function reload() {
    if (!id) return;
    dataService.getCreator(id).then((c) => {
      setCreator(c ?? null);
      if (c) pushRecentlyViewed({ id: c.id, label: `${c.id} — ${c.fullName}`, route: `/creators/${c.id}` });
    });
  }
  useEffect(reload, [id]);

  if (creator === undefined) return <div className="card" style={{ padding: 20 }}>Loading…</div>;
  if (creator === null) {
    return (
      <div className="card" style={{ padding: 20 }}>
        <div style={{ fontWeight: 700 }}>Creator not found</div>
        <button onClick={() => navigate('/creators')} style={{ marginTop: 10 }}>Back to Creators</button>
      </div>
    );
  }

  async function handleEditSubmit(values: CreatorFormValues) {
    if (!creator) return;
    await dataService.updateCreator(creator.id, values);
    push(t('toast.creatorUpdated'), 'success');
    setEditing(false);
    reload();
  }

  async function addTimelineNote() {
    if (!creator || !noteText.trim()) return;
    await dataService.addCreatorTimelineEntry(creator.id, t('detail.manualActivity'), noteText.trim());
    setNoteText('');
    push(t('toast.activityAdded'), 'success');
    reload();
  }

  async function scheduleFollowup() {
    if (!creator) return;
    const dueDate = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10);
    await dataService.createFollowup({ entityType: 'creator', entityId: creator.id, entityLabel: creator.fullName, title: t('followupForm.defaultTitle'), dueDate, priority: 'medium', ownerId: creator.ownerId });
    push(t('toast.followupScheduled'), 'success');
    navigate('/followups');
  }

  return (
    <div className="print-area">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div className="avatar-circle">{creator.fullName.slice(0, 1)}</div>
          <div>
            <h1 style={{ margin: 0 }}>{creator.fullName}</h1>
            <div style={{ color: 'var(--text-3)', fontSize: 13 }}>{creator.id} <CopyButton value={creator.id} /> · {creator.tiktokUsername} · <StatusBadge entity="creator" statusKey={creator.status} /> {creator.riskLevel !== 'low' && <span className={`badge ${creator.riskLevel === 'high' ? 'crit' : 'warn'}`}>{creator.riskLevel} risk</span>}</div>
          </div>
        </div>
        <div className="no-print" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setEditing(true)}>{t('rowMenu.edit')}</button>
          <button onClick={scheduleFollowup}>{t('followupForm.schedule')}</button>
          <button onClick={() => {
            exportRowsToCsv(creator.id, [creator], [
              { key: 'id', header: 'ID', value: (c) => c.id },
              { key: 'name', header: 'Name', value: (c) => c.fullName },
              { key: 'status', header: 'Status', value: (c) => c.status },
            ]);
            dataService.logExport('Creator detail');
          }}>{t('common.export')}</button>
          <button onClick={() => window.print()}>{t('common.print')}</button>
          <button style={{ color: 'var(--warn)' }} onClick={() => setConfirmArchive(true)}>{t('rowMenu.archive')}</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>Overview</div>
          <p><b>Phone:</b> <SafeLink href={`tel:${creator.phone}`}>{creator.phone || '—'}</SafeLink></p>
          <p><b>WhatsApp:</b> {creator.whatsapp ? <SafeLink href={`tel:${creator.whatsapp}`}>{creator.whatsapp}</SafeLink> : '—'}</p>
          <p><b>Email:</b> <SafeLink href={`mailto:${creator.email}`}>{creator.email || '—'}</SafeLink></p>
          <p><b>Country / Emirate:</b> {creator.country} · {creator.emirate}</p>
          <p><b>Owner:</b> {creator.ownerId}</p>
          <p><b>{t('detail.nextAction')}:</b> {creator.nextAction ?? '—'}</p>
          <p><b>{t('detail.nextFollowup')}:</b> {creator.nextFollowup ?? '—'}</p>
          {creator.notes && <p><b>Notes:</b> {creator.notes}</p>}
        </div>

        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>{t('detail.creatorTimeline')}</div>
          <div className="no-print" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input className="text-input" placeholder={t('detail.addNotePlaceholder')} value={noteText} onChange={(e) => setNoteText(e.target.value)} />
            <button className="btn-primary" onClick={addTimelineNote}>{t('common.save')}</button>
          </div>
          {creator.timeline.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-3)' }}>{t('detail.noTimelineYet')}</div>
          ) : (
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {creator.timeline.map((entry) => (
                <div key={entry.id} style={{ borderBottom: '1px solid var(--border)', padding: '8px 0', fontSize: 13 }}>
                  <div style={{ fontWeight: 600 }}>{entry.label}</div>
                  {entry.note && <div style={{ color: 'var(--text-2)' }}>{entry.note}</div>}
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{entry.actor} · {new Date(entry.createdAt).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card" style={{ padding: 16, gridColumn: '1 / -1' }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>{t('detail.creatorPerformance')}</div>
          {creator.followers != null ? (
            <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
              <div><div style={{ color: 'var(--text-3)' }}>Followers</div><div style={{ fontWeight: 700 }}>{creator.followers.toLocaleString()}</div></div>
              <div><div style={{ color: 'var(--text-3)' }}>LIVE hours (30d)</div><div style={{ fontWeight: 700 }}>{creator.liveHours ?? '—'}</div></div>
              <div><div style={{ color: 'var(--text-3)' }}>Last LIVE</div><div style={{ fontWeight: 700 }}>{creator.lastLive ?? '—'}</div></div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text-3)' }}>{t('common.requiresBackend')} — social/TikTok connector is {t('common.notConnected').toLowerCase()}.</div>
          )}
        </div>
      </div>

      {editing && <CreatorFormDrawer creator={creator} onClose={() => setEditing(false)} onSubmit={handleEditSubmit} />}

      <ConfirmModal
        open={confirmArchive}
        title={t('confirm.archiveCreatorTitle')}
        recordName={`${creator.id} — ${creator.fullName}`}
        description={t('confirm.archiveCreatorDesc')}
        reversible
        confirmLabel={t('rowMenu.archive')}
        onConfirm={async () => { await dataService.archiveCreator(creator.id); push(t('toast.creatorArchived'), 'warn'); navigate('/creators'); }}
        onCancel={() => setConfirmArchive(false)}
      />
    </div>
  );
}
